/**
 * CameraSwitchButton — digital mode.
 *
 * Renders a small "switch camera" affordance in the chrome bar:
 *   - Mobile: toggles `facingMode` between 'user' (front) and 'environment'
 *     (back) and re-acquires the stream.
 *   - Desktop: cycles to the next connected video-input device (mirrors the
 *     operator SettingsModal camera dropdown, as a one-tap advance). Only
 *     shown when the browser reports 2+ cameras — a single-camera laptop has
 *     nothing to switch to.
 *
 * Either way the choice persists to localStorage so a re-open keeps it.
 * See docs/digital-mode-plan.md §2.4.
 */

import React, { useEffect, useState } from 'react'
import { FaSyncAlt } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { isDigitalMode, isMobilePlatform } from '@renderer/lib/runtime'
import {
  switchDigitalCamera,
  getDigitalFacingMode,
  getDigitalCameraCount
} from '@renderer/lib/digitalCamera'

interface CameraSwitchButtonProps {
  /** Optional className passed by parent — keep it composable in different chrome layouts. */
  className?: string
  iconClassName?: string
}

export const CameraSwitchButton: React.FC<CameraSwitchButtonProps> = ({
  className,
  iconClassName
}) => {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  // Desktop only: number of connected cameras. Mobile ignores this (front/back
  // is a facingMode toggle, not a device count). Starts at 0 so the button
  // stays hidden until enumeration resolves.
  const [desktopCameraCount, setDesktopCameraCount] = useState(0)

  const digital = isDigitalMode()
  const mobile = isMobilePlatform()

  // Desktop: enumerate cameras once so we know whether a switch is even
  // possible. `devicechange` re-runs it when a camera is plugged/unplugged.
  useEffect(() => {
    if (!digital || mobile) return
    let cancelled = false
    const refresh = (): void => {
      getDigitalCameraCount().then((count) => {
        if (!cancelled) setDesktopCameraCount(count)
      })
    }
    refresh()
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh)
    return (): void => {
      cancelled = true
      navigator.mediaDevices?.removeEventListener?.('devicechange', refresh)
    }
  }, [digital, mobile])

  // Hide outside digital mode entirely. On desktop, also hide unless there are
  // at least two cameras to cycle between.
  if (!digital) return null
  if (!mobile && desktopCameraCount < 2) return null

  const current = mobile ? getDigitalFacingMode() : null
  const titleKey = mobile
    ? current === 'user'
      ? 'controls.useRearCamera'
      : 'controls.useFrontCamera'
    : 'controls.switchCamera'
  const titleDefault = mobile
    ? current === 'user'
      ? 'Use rear camera'
      : 'Use front camera'
    : 'Switch camera'

  const handleClick = async (): Promise<void> => {
    if (busy) return
    setBusy(true)
    try {
      await switchDigitalCamera()
    } catch (err) {
      // Re-throwing isn't useful in a toolbar button; log and let the user retry.
      console.error('[CameraSwitchButton] switch failed:', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      title={t(titleKey, titleDefault)}
      onClick={handleClick}
      disabled={busy}
      className={
        className ??
        'text-carbon/75 hover:text-carbon transition-colors inline-flex items-center justify-center min-h-9 min-w-9'
      }
      aria-label={t(titleKey, titleDefault)}
    >
      <FaSyncAlt
        className={`${iconClassName ?? 'text-[1.25rem]'} ${busy ? 'animate-spin opacity-50' : ''}`}
      />
    </button>
  )
}

export default CameraSwitchButton
